import SubmissionRow from './SubmissionRow.jsx';

const COLUMNS = [
  { key: 'select', label: '' },
  { key: 'actions', label: '' },
  { key: 'received', label: 'Received', sortKey: 'received_at' },
  { key: 'who', label: 'Name / Email' },
  { key: 'problem_summary', label: 'Problem Summary' },
  { key: 'severity', label: 'Severity', sortKey: 'severity' },
  { key: 'sentiment', label: 'Sentiment' },
  { key: 'status', label: 'Status' },
  { key: 'assigned_to_name', label: 'Assigned To' },
  { key: 'follow_up', label: 'Follow-Up' },
];

function SkeletonRow({ columns }) {
  return (
    <tr>
      {columns.map((c, i) => (
        <td key={c.key + i}>
          {c.key === 'select' ? null : (
            <div className="skeleton-cell" style={{ width: i === 3 ? '80%' : '60%' }} />
          )}
        </td>
      ))}
    </tr>
  );
}

export default function SubmissionTable({
  submissions,
  loading,
  isAdmin,
  onStatusChange,
  onAssigneeChange,
  sortBy,
  sortOrder,
  onSortChange,
  page,
  pageSize,
  total,
  pages,
  onPageChange,
  onClearFilters,
  selectedIds,
  onToggleSelect,
  onToggleAllVisible,
  columnVisibility,
}) {
  // Hidden columns are removed from the DOM entirely (not CSS-hidden) so
  // the table actually reclaims the horizontal space. Columns without a
  // visibility entry (select/received/problem_summary/actions) always show.
  const visibleColumns = COLUMNS.filter(
    (c) => columnVisibility?.[c.key] !== false
  );
  const hasSelection = selectedIds && selectedIds.size > 0;
  const visibleIds = submissions.map((s) => s.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds?.has(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selectedIds?.has(id));
  const showSkeleton = loading && submissions.length === 0;
  const showEmpty = !loading && submissions.length === 0;

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function toggleSort(sortKey) {
    if (!sortKey) return;
    if (sortBy === sortKey) {
      onSortChange(sortKey, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(sortKey, 'desc');
    }
  }

  function sortIndicator(sortKey) {
    if (!sortKey || sortBy !== sortKey) return null;
    return sortOrder === 'asc' ? ' ▲' : ' ▼';
  }

  return (
    <div className={`table-wrap${hasSelection ? ' table-wrap--has-selection' : ''}`}>
      <div className="table-scroll">
      <table className="submissions">
        <thead>
          <tr>
            {visibleColumns.map((c) => {
              if (c.key === 'select') {
                return (
                  <th key="select" className="col-select">
                    <input
                      type="checkbox"
                      className="submission-checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someVisibleSelected;
                      }}
                      onChange={onToggleAllVisible}
                      aria-label="Select all visible rows"
                    />
                  </th>
                );
              }
              return (
                <th
                  key={c.key}
                  className={c.sortKey ? 'sortable' : undefined}
                  onClick={c.sortKey ? () => toggleSort(c.sortKey) : undefined}
                >
                  {c.label}
                  {sortIndicator(c.sortKey)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {showSkeleton &&
            Array.from({ length: 6 }).map((_, i) => (
              <SkeletonRow key={i} columns={visibleColumns} />
            ))}
          {!showSkeleton &&
            submissions.map((s) => (
              <SubmissionRow
                key={s.id}
                submission={s}
                isAdmin={isAdmin}
                onStatusChange={onStatusChange}
                onAssigneeChange={onAssigneeChange}
                selected={selectedIds?.has(s.id) || false}
                onToggleSelect={onToggleSelect}
                columnVisibility={columnVisibility}
                isNew={
                  (s.status === 'unreviewed' || s.status === 'new') &&
                  !s.assigned_to_name
                }
              />
            ))}
          {showEmpty && (
            <tr>
              <td colSpan={visibleColumns.length}>
                <div className="empty-state">
                  No submissions match your filters.
                  <div style={{ marginTop: 12 }}>
                    <button className="btn btn--secondary" onClick={onClearFilters}>
                      Clear filters
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      {!showEmpty && (
        <div className="table__pagination">
          <div>
            Showing {start}–{end} of {total} result{total === 1 ? '' : 's'}
          </div>
          <div className="table__pagination-buttons">
            <button
              className="btn btn--secondary"
              disabled={page <= 1 || loading}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </button>
            <button
              className="btn btn--secondary"
              disabled={page >= pages || loading || total === 0}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
